import { IQuotePricingConfig } from 'types/quoteEditor.types';

export interface IPriceCalculationResult {
  platinum: string;
  finalPrice: string;
}

export interface IInversePriceCalculationResult {
  margin: string;
  finalPrice: string;
}

/**
 * Aplica el redondeo FLOOR al múltiplo inferior más cercano de `factor`.
 * Ejemplo: floorTo(82.7, 5) = 80
 */
const floorTo = (value: number, factor: number) => Math.floor(value / factor) * factor;

/**
 * Calcula el Precio Platinum y el Precio Final a partir de costo y margen.
 * Fórmula: Platinum = (Costo × (1 + IVA)) / Tasa_cambio / (1 - Margen%)
 * Precio Final:
 *   - planMultiplier === 1: Platinum exacto (sin redondeo), ya que totalPrice debe coincidir con Platinum.
 *   - planMultiplier > 1:   FLOOR(Platinum × planMultiplier, 5)
 *
 * Esta función es el punto de migración al endpoint de backend (BORD-5548).
 */
export const calculatePrices = (
  costWithoutTax: number,
  marginPercent: number,
  config: IQuotePricingConfig
): IPriceCalculationResult => {
  const { taxRate, planMultiplier, exchangeRate = 1 } = config;
  // Cuando exchangeRate aún no está disponible se usa 1 (sin conversión de moneda)
  // como valor provisional hasta que el backend (BORD-5548) asuma el cálculo.
  if (!costWithoutTax || marginPercent >= 100) return { platinum: '', finalPrice: '' };
  const safePlanMultiplier = typeof planMultiplier === 'number' && isFinite(planMultiplier) ? planMultiplier : 1;
  const marginDecimal = marginPercent / 100;
  const platinum = (costWithoutTax * (1 + taxRate)) / exchangeRate / (1 - marginDecimal);
  if (!isFinite(platinum) || isNaN(platinum)) return { platinum: '', finalPrice: '' };
  const finalPrice = safePlanMultiplier === 1 ? platinum : floorTo(platinum * safePlanMultiplier, 5);
  return { platinum: platinum.toFixed(2), finalPrice: isNaN(finalPrice) ? '' : finalPrice.toFixed(2) };
};

/**
 * Calcula el Margen implícito y el Precio Final a partir de un Precio Platinum editado manualmente.
 * Fórmula inversa: Margen% = 1 - [(Costo × (1 + IVA)) / Tasa_cambio / Platinum]
 *
 * Esta función es el punto de migración al endpoint de backend (BORD-5548).
 */
export const calculatePricesInverse = (
  costWithoutTax: number,
  platinum: number,
  config: IQuotePricingConfig
): IInversePriceCalculationResult => {
  const { taxRate, planMultiplier, exchangeRate = 1 } = config;
  const safePlanMultiplier = typeof planMultiplier === 'number' && isFinite(planMultiplier) ? planMultiplier : 1;
  if (!platinum) return { margin: '', finalPrice: '' };
  const marginDecimal = 1 - (costWithoutTax * (1 + taxRate)) / exchangeRate / platinum;
  const finalPrice = floorTo(platinum * safePlanMultiplier, 5);
  return { margin: isNaN(marginDecimal) ? '' : (marginDecimal * 100).toFixed(2), finalPrice: isNaN(finalPrice) ? '' : finalPrice.toFixed(2) };
};
